import { Card, CardProps } from '@components/card';
import { FormLabel } from '@components/form-label';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconEdit } from '@components/icons/edit';
import { IconMerge } from '@components/icons/merge';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useCreateTravellerActionRelationship,
  useJourneyTravellerAction,
  useRemoveTravellerActionRelationship,
  useTravellerActionRelationships,
  useTravellerJourneyActions,
} from 'api/traveller-actions/hooks';
import { BASE_ACTION_TYPE, TravellerJourneyAction } from 'api/types';

import { DirectLine, RelationshipLine, ignoreTypes } from './journey-sidebar';

import { ActionIcon } from '../action-icon';
import { ActionLabel, actionLabels } from '../action-label';
import { SidebarProps, useEditingContext } from '../common';

type ActionType = 'journey_merge';

type RelationshipAction = {
  relationshipShortId: string | null;
  action: BASE_ACTION_TYPE;
  status: TravellerJourneyAction['status'];
  description?: string;
};

const relationshipActionStatusMap: {
  [key in TravellerJourneyAction['status']]: CardProps['variant'];
} = {
  completed: 'green',
  in_progress: 'orange',
  waiting_to_be_started: 'indigo',
  halted: 'green',
  upcoming: 'default',
  skipped: 'gray',
};

const SmallActionCard = ({
  action,
  shortId,
  status,
  description,
  isDeletable = false,
}: {
  action: BASE_ACTION_TYPE;
  shortId?: string | null;
  status: TravellerJourneyAction['status'];
  description?: string;
  isDeletable?: boolean;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const removeMutation = useRemoveTravellerActionRelationship();
  const showDeleteModal = useDeleteModal();

  return (
    <Card spaceX={4} spaceY={2} variant={relationshipActionStatusMap[status]}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          <ActionIcon action={action} />
          <div>
            <ActionLabel action={action} font="semibold14" />
            {description && <Text font="regular14" color="textSecondary" text={description} />}
          </div>
        </Stack>
        {shortId && isDeletable && (
          <IconButton
            onClick={() =>
              showDeleteModal({
                title: t('deleteRelationship'),
                description: t('deleteRelationshipDescription'),
                buttonText: t('delete'),
                hideCannotBeUndone: true,
                onConfirm: () => removeMutation.mutateAsync(shortId),
              })
            }
            icon={<IconTrash />}
            variant="ghost-purple"
          />
        )}
      </Stack>
    </Card>
  );
};

const findParentAction = (
  actions: TravellerJourneyAction[],
  shortId: string | undefined
): any | undefined => {
  if (!shortId) return undefined;

  const action = actions.find((a) => a.shortId === shortId);
  if (!action || !['journey_branch', 'branch_base'].includes(action.action)) return action;

  return findParentAction(actions, action.parentAction);
};

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const addMutation = useCreateTravellerActionRelationship();
  const { isEditing, startEditing, stopEditing } = useEditingContext();

  const actionQuery = useJourneyTravellerAction<ActionType>(shortId);
  const relationshipsQuery = useTravellerActionRelationships(shortId);
  const travellerActionsQuery = useTravellerJourneyActions(
    actionQuery.data?.travellerJourneyId ?? ''
  );

  const action = actionQuery.data;

  if (!relationshipsQuery.data || !action || !travellerActionsQuery.data) {
    return <Spinner />;
  }

  const parentAction = findParentAction(travellerActionsQuery.data, action.parentAction);
  if (!parentAction) {
    return <Spinner />;
  }
  const relationshipActions: RelationshipAction[] = [];

  const relationshipShortIds = relationshipsQuery.data.actions.map(
    (relationship) => relationship.parentAction
  );

  relationshipsQuery.data.actions.forEach((relationship) => {
    const action = travellerActionsQuery.data.find(
      (travellerAction) => travellerAction.shortId === relationship.parentAction
    );
    if (action) {
      relationshipActions.push({
        relationshipShortId: relationship.shortId,
        action: action.action,
        status: action.status,
        description: action.description,
      });
    }
  });

  const validMergeNodes = travellerActionsQuery.data.filter(
    (travellerAction) =>
      !ignoreTypes.includes(travellerAction.action) &&
      travellerAction.shortId !== shortId &&
      !action.ancestors.includes(travellerAction.shortId) &&
      !action.descendants.includes(travellerAction.shortId) &&
      !relationshipShortIds.includes(travellerAction.shortId)
  );

  const handleSave = ({
    parentShortId,
    childShortId,
  }: {
    parentShortId: string;
    childShortId: string;
  }) => {
    return addMutation.mutateAsync({ parentShortId, childShortId });
  };

  const relationshipOptions = validMergeNodes.map((node) => {
    return {
      value: node.shortId,
      helpText: node.description || t('noDescription'),
      label: (
        <Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <ActionIcon action={node.action} />
            {actionLabels[node.action]}
          </Stack>
        </Stack>
      ),
    };
  });

  const canEdit = action.status !== 'completed';
  const isEditable = isEditing && canEdit;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconMerge} iconColor="orange100" />
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={2}>
            <FormLabel text={t('previous')} helpText={t('previousHelpText')} />
            <DirectLine />
            <SmallActionCard
              status={parentAction.status}
              key={parentAction.shortId}
              action={parentAction.action}
              description={parentAction.description || t('noDescription')}
            />
          </Stack>

          <Stack space={4} orientation="horizontal" style={{ width: '100%' }} align="center">
            <Separator />
            <Text font="semibold14" color="textHelper" text={t('core.and')} />
            <Separator />
          </Stack>

          <Stack space={4}>
            <Text text={t('selectedActions')} font="semibold14" />
            <RelationshipLine />
            <Stack space={2}>
              {relationshipActions.map((action) => (
                <SmallActionCard
                  key={action.relationshipShortId}
                  action={action.action}
                  description={action.description || t('noDescription')}
                  shortId={action.relationshipShortId}
                  status={action.status}
                  isDeletable={isEditable}
                />
              ))}
            </Stack>
            <Text text={t('selectedActionsNeeds')} font="regular14" />
            {isEditable && (
              <Select
                noOptions={() => t('noRelationships')}
                label={t('addRelationship')}
                options={relationshipOptions}
                onChange={(option) =>
                  option && handleSave({ childShortId: shortId, parentShortId: option })
                }
              />
            )}

            {canEdit && (
              <Sidebar.Footer>
                <IconButton
                  variant="ghost-purple"
                  icon={isEditing ? <IconArrowLeft /> : <IconEdit />}
                  onClick={isEditing ? stopEditing : startEditing}
                />
              </Sidebar.Footer>
            )}
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    noRelationships: 'No available actions',
    previous: 'Previous action',
    previousNeeds: 'Needs to be completed or manually skipped',
    previousHelpText: '(In workflow pipeline)',
    noDescription: 'No description',
    title: 'Wait for other actions',
    selectedActions: 'Selected actions',
    selectedActionsNeeds: 'Need to be completed or manually / conditionally skipped',
    addRelationship: 'Add action',
    deleteRelationship: 'Delete condition',
    deleteRelationshipDescription:
      'Are you sure you want to delete this condition? The journey might continue if it is the last one remaning',
    delete: 'Delete',
  },
};
